"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-accent-light/20 border border-accent rounded-lg">
        <p className="text-foreground text-center mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid gap-4">
        <FormField control={control} name="name" rules={{
        required: "Your name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormDescription>First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        minLength: {
          value: 10,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone / WhatsApp</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-123-4567" {...field} />
              </FormControl>
              <FormDescription>Include country code for international callers</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service_needed" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Service Needed</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="video_production">Video Production</SelectItem>
                  <SelectItem value="photography">Photography</SelectItem>
                  <SelectItem value="event_coverage">Event Coverage</SelectItem>
                  <SelectItem value="social_media_content">Social Media Content</SelectItem>
                  <SelectItem value="post_production">Post‑Production</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_details" rules={{
        required: "Please provide project details",
        minLength: {
          value: 10,
          message: "Please provide more details (at least 10 characters)"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Project Details</FormLabel>
              <FormControl>
                <Textarea placeholder="Brief description of your project, goals, and any special requirements..." rows={4} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget_range" render={({
        field
      }) => <FormItem>
              <FormLabel>Budget Range</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a budget range" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="0_5000">$0 – $5,000</SelectItem>
                  <SelectItem value="5001_10000">$5,001 – $10,000</SelectItem>
                  <SelectItem value="10001_20000">$10,001 – $20,000</SelectItem>
                  <SelectItem value="20001_plus">$20,001+</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel>Preferred Start Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormDescription>If you have a target date for the project</FormDescription>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full btn-primary">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending Request...
            </> : "Get Your Quote"}
        </Button>
      </div>
    </Form>;
}
export default function GetAQuoteCta() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const {
    ref: buttonRef,
    isVisible: buttonVisible
  } = useRevealOnScroll({
    threshold: 0.3,
    once: true
  });
  return <section id="get-a-quote-cta" className="relative min-h-[30vh] flex items-center justify-center py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="absolute inset-0 bg-dark-background" style={{
      background: "linear-gradient(135deg, hsl(0, 0%, 15%) 0%, hsl(0, 0%, 12%) 50%, hsl(38, 80%, 10%) 100%)"
    }} />
      <div className="absolute inset-0 surface-overlay" />

      <div className="relative z-10 max-w-3xl mx-auto text-center">
        <div ref={headlineRef} className={`transition-all duration-[var(--duration-medium)] ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground-dark mb-4">
            Let's Create Something Powerful
          </h2>
          <p className="text-lg sm:text-xl text-foreground-dark/90 mb-8 leading-relaxed">
            Premium content that looks expensive, feels emotional, and converts. Get your custom quote today.
          </p>
        </div>

        <div ref={buttonRef} className={`transition-all duration-[var(--duration-medium)] delay-100 ${buttonVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button onClick={() => setIsDialogOpen(true)} className="btn-primary text-lg px-8 py-6 h-auto shadow-strong">
            Get Your Quote
          </Button>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-display">Get a Quote Form</DialogTitle>
            <DialogDescription>
              Tell us about your project and we'll provide you with a custom quote tailored to your needs.
            </DialogDescription>
          </DialogHeader>
          <FormProvider formId="quote_form_services" sectionName="get-a-quote-cta" defaultValues={{
          name: "",
          email: "",
          phone: "",
          service_needed: "",
          project_details: "",
          budget_range: "",
          preferred_date: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            minLength: 10
          },
          service_needed: {
            required: true
          },
          project_details: {
            required: true,
            minLength: 10
          }
        }} submitText="Get Your Quote" successMessage="Thank you! We've received your request and will get back to you within 24 hours with a custom quote." errorMessage="Something went wrong. Please try again or contact us directly.">
            <QuoteForm />
          </FormProvider>
        </DialogContent>
      </Dialog>
    </section>;
}